(() => {
  'use strict';
  if (window.SMGlassScroll) return;
  const script = document.querySelector('script[data-sm-glass-scroll]');
  if (!script || script.dataset.smGlassScroll !== 'true') return;

  // Explicit surface families plus the theme's native glass panels. Computed
  // material checks exclude the plain sections left by earlier redesigns.
  const selectors = [
    '.sm-rewards[data-liquid-glass="true"] .bb-rewards-signin a:first-child',
    '.sm-rewards[data-liquid-glass="true"] .sm-rewards-primary',
    '.sm-rewards[data-liquid-glass="true"] .sm-rewards-membership',
    '.sm-rewards[data-liquid-glass="true"] .bb-reward-card',
    '.bb-global-header[data-sm-desktop-design="true"] .bb-site-header',
    '[data-sm-side-menu-design="true"] .bb-quick-access__drawer',
    '[data-sm-glass-surface-card]',
    '.sm-home__mobile-countdown', '.bbce__countdown',
    '.product-information[data-sm-liquid-controls="true"] .product-form-buttons .add-to-cart-button',
    '.product-information[data-sm-liquid-controls="true"] .product-form-buttons .quantity-selector',
    '.product-information[data-sm-liquid-controls="true"] .product-form-buttons .bb-membership-wishlist',
    '.bb-liquid-panel', '.bb-liquid-button',
    '.bbmh[data-sm-dashboard-glass="true"] .bbmh-glass', '.bb-auth[data-sm-dashboard-glass="true"] .bb-auth__panel'
  ].join(',');
  const preferences = [
    '(prefers-reduced-motion: reduce)', '(prefers-reduced-transparency: reduce)',
    '(prefers-contrast: more)', '(forced-colors: active)'
  ].map(query => matchMedia(query));
  const surfaces = new Map();
  const visible = new Set();
  let frame = 0;
  let active = false;
  let dirty = false;
  let lastFrame = 0;
  let lastInput = -Infinity;
  let sourcePosition = window.scrollY;
  let targetVelocity = 0;
  let velocity = 0;
  let strength = 0;
  let lastDelta = 0;
  let lastSource = document;
  const scrollPositions = new WeakMap();
  scrollPositions.set(document, {position:window.scrollY, time:performance.now()});
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const observer = new IntersectionObserver(entries => {
    entries.forEach(({target, isIntersecting}) => {
      if (!surfaces.has(target)) return;
      if (isIntersecting) { visible.add(target); dirty = true; }
      else {
        visible.delete(target);
        surfaces.get(target)?.plane.removeAttribute('data-lit');
      }
    });
  });

  function isGlass(element) {
    if (element.matches('.product-information__media') || element.querySelector('.bb-tees-gloss-edge')) return false;
    const style = getComputedStyle(element);
    // Never change a containing block: fixed drawers depend on their ancestors.
    if (style.position === 'static') return false;
    // The supplied clear-glass renderer uses video/refraction and rim light on
    // mobile rather than backdrop-filter. Its ready flag still denotes glass.
    if (element.matches('[data-sm-glass-surface-ready="true"],.sm-home__mobile-countdown')) return true;
    if (style.backdropFilter && style.backdropFilter !== 'none') return true;
    return [getComputedStyle(element, '::before'), getComputedStyle(element, '::after')]
      .some(material => material.display !== 'none' && material.backdropFilter && material.backdropFilter !== 'none');
  }

  function remove(element) {
    observer.unobserve(element);
    surfaces.get(element)?.plane.remove();
    element.removeAttribute('data-sm-scroll-glass');
    surfaces.delete(element);
    visible.delete(element);
  }

  function scan(root = document) {
    if (!active) return;
    const candidates = [...root.querySelectorAll(selectors)];
    if (root instanceof Element && root.matches(selectors)) candidates.unshift(root);
    candidates.forEach(element => {
      if (surfaces.has(element) || !isGlass(element)) return;
      const plane = document.createElement('span');
      plane.setAttribute('data-sm-scroll-reflection', '');
      plane.setAttribute('aria-hidden', 'true');
      plane.setAttribute('inert', '');
      const light = document.createElement('span');
      light.className = 'sm-glass-scroll__light';
      const left = document.createElement('span');
      left.className = 'sm-glass-scroll__light sm-glass-scroll__light--left';
      const rim = document.createElement('span');
      rim.className = 'sm-glass-scroll__rim';
      rim.append(light, left);
      plane.append(rim);
      element.append(plane);
      element.setAttribute('data-sm-scroll-glass', '');
      surfaces.set(element, {plane, distance:0, speed:0, baseDistance:0, weight:0, targetWeight:0, width:0, height:0, radius:0, sideInset:0});
      // Prime real nested scroll containers, including the theme's page wrapper.
      for (let node = element; node; node = node.parentElement) {
        if (node.scrollHeight > node.clientHeight && /auto|scroll/.test(getComputedStyle(node).overflowY)) {
          if (!scrollPositions.has(node)) scrollPositions.set(node, {position:node.scrollTop, time:performance.now()});
          break;
        }
      }
      observer.observe(element);
    });
  }

  function stop() {
    cancelAnimationFrame(frame);
    frame = 0; lastFrame = 0; strength = 0; velocity = 0; targetVelocity = 0;
    visible.forEach(element => {
      const state = surfaces.get(element);
      if (state) { state.plane.removeAttribute('data-lit'); state.weight = 0; }
    });
  }

  // Exact critically damped spring step. Re-targeting retains presentation
  // position and velocity, so reversing a scroll never restarts an animation.
  function spring(position, speed, target, dt) {
    const omega = 18;
    const offset = position - target;
    const carry = speed + omega * offset;
    const decay = Math.exp(-omega * dt);
    return [target + (offset + carry * dt) * decay, (speed - omega * carry * dt) * decay];
  }

  // The path starts halfway around the upper corner and finishes halfway
  // around the lower corner. Both glints remain on the physical rim.
  function sidePoint(y, width, height, radius) {
    const centerY = y < radius ? radius : y > height - radius ? height - radius : y;
    const offset = y - centerY;
    const reach = Math.sqrt(Math.max(0, radius * radius - offset * offset));
    const rightX = width - radius + reach;
    const slope = reach ? -offset / reach : 0;
    return {rightX:rightX + .75, leftX:width - rightX + .75, y:y + .75,
      angle:Math.atan2(1, slope) * 180 / Math.PI, leftAngle:Math.atan2(1, -slope) * 180 / Math.PI,
      corner:radius ? clamp(Math.abs(offset) / (radius * Math.SQRT1_2), 0, 1) : 0};
  }

  // A shared light level passes through successive glass rows. Across a gap,
  // the lower corners relinquish light as the next upper corners receive it.
  // This is a position mapping, so upward scrolling retraces the same path.
  function flowTargets(rows, level) {
    const targets = rows.map(row => {
      const height = row.bottom - row.top;
      const cornerHold = Math.min(height * .12, 28);
      return {progress:clamp((level - row.top - cornerHold) / (height - 2 * cornerHold), 0, 1), weight:0};
    });
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i], next = rows[i + 1];
      if (next) {
        const overlap = clamp(Math.min(row.bottom - row.top, next.bottom - next.top) * .12, 8, 28);
        const start = Math.min(row.bottom, next.top) - overlap;
        const end = Math.max(row.bottom, next.top) + overlap;
        if (level >= start && level <= end) {
          const t = clamp((level - start) / (end - start), 0, 1);
          const eased = t * t * (3 - 2 * t);
          targets[i].weight = 1 - eased;
          targets[i + 1].weight = eased;
          return targets;
        }
      }
      if (level <= row.bottom || !next) { targets[i].weight = 1; return targets; }
    }
    return targets;
  }

  function measure() {
    dirty = false;
    const viewport = window.innerHeight;
    // Read geometry once per scroll input, never throughout the settling tail.
    const readings = [...visible].map(element => ({element, rect:element.getBoundingClientRect(), radius:getComputedStyle(element).borderTopLeftRadius}));
    const rows = [];
    const independent = [];
    readings.sort((a, b) => a.rect.top - b.rect.top).forEach(({element, rect, radius}) => {
      if (!element.isConnected) { remove(element); return; }
      const state = surfaces.get(element);
      const follows = lastSource === document || lastSource.contains(element) || element === lastSource;
      state.eligible = follows && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < viewport;
      if (!state.eligible) { state.plane.removeAttribute('data-lit'); return; }
      state.width = rect.width - 1.5;
      state.height = rect.height - 1.5;
      const outerRadius = (parseFloat(radius) || 0) * (radius?.includes('%') ? Math.min(rect.width, rect.height) / 100 : 1);
      state.radius = clamp(outerRadius - .75, 0, Math.min(state.width, state.height) / 2);
      state.sideInset = state.radius * (1 - Math.SQRT1_2);
      state.targetWeight = 0;
      state.plane.style.setProperty('--sm-edge-length', `${clamp(Math.min(rect.width, rect.height) * .18, 40, 92).toFixed(2)}px`);
      if (element.matches('.bb-site-header,.bb-quick-access__drawer,.bbmh__mobile-nav,.bbmh__more')) {
        independent.push(state);
      } else {
        const row = rows[rows.length - 1];
        if (row && Math.abs(row.top - rect.top) < 4) {
          row.bottom = Math.max(row.bottom, rect.bottom); row.members.push({state, rect});
        } else rows.push({top:rect.top, bottom:rect.bottom, members:[{state, rect}]});
      }
    });
    const level = viewport * .58;
    const targets = flowTargets(rows, level);
    rows.forEach((row, index) => row.members.forEach(({state}) => {
      const progress = targets[index].progress;
      state.baseDistance = state.sideInset + (state.height - 2 * state.sideInset) * progress;
      state.targetWeight = targets[index].weight;
    }));
    independent.forEach(state => {
      const progress = clamp(sourcePosition / 650, 0, 1);
      state.baseDistance = state.sideInset + (state.height - 2 * state.sideInset) * progress;
      state.targetWeight = 1;
    });
  }

  function update(now) {
    frame = 0;
    if (!active || document.hidden) { stop(); return; }
    if (dirty) measure();
    const dt = clamp(lastFrame ? (now - lastFrame) / 1000 : 1 / 60, .001, 1 / 30);
    lastFrame = now;
    const receiving = now - lastInput < 110;
    velocity += ((receiving ? targetVelocity : 0) - velocity) * (1 - Math.exp(-dt / .085));
    const speed = clamp(Math.abs(velocity) / 1800, 0, 1);
    const desiredStrength = receiving ? .72 + speed * .25 : 0;
    strength += (desiredStrength - strength) * (1 - Math.exp(-dt / (receiving ? .035 : .36)));
    let moving = false;
    let rendered = 0;
    visible.forEach(element => {
      const state = surfaces.get(element);
      if (!state?.eligible) return;
      const {plane} = state;
      // A newly receiving panel starts at its current scroll position while
      // still unlit; already visible reflections retain spring momentum.
      if (state.weight < .001 && state.targetWeight > 0) {
        state.distance = state.baseDistance; state.speed = 0;
      }
      state.weight += (state.targetWeight - state.weight) * (1 - Math.exp(-dt / .07));
      if (state.weight < .001 && state.targetWeight === 0) { plane.removeAttribute('data-lit'); return; }
      rendered++;
      const targetDistance = clamp(state.baseDistance + Math.tanh(velocity / 1200) * 3, state.sideInset, state.height - state.sideInset);
      [state.distance, state.speed] = spring(state.distance, state.speed, targetDistance, dt);
      moving ||= Math.abs(state.distance - targetDistance) > .02 || Math.abs(state.weight - state.targetWeight) > .002;
      const point = sidePoint(clamp(state.distance, state.sideInset, state.height - state.sideInset), state.width, state.height, state.radius);
      plane.style.setProperty('--sm-glass-x', `${point.rightX.toFixed(2)}px`);
      plane.style.setProperty('--sm-glass-left-x', `${point.leftX.toFixed(2)}px`);
      plane.style.setProperty('--sm-glass-y', `${point.y.toFixed(2)}px`);
      plane.style.setProperty('--sm-glass-angle', `${point.angle.toFixed(2)}deg`);
      plane.style.setProperty('--sm-glass-left-angle', `${point.leftAngle.toFixed(2)}deg`);
      plane.style.setProperty('--sm-glass-stretch', (1 + speed * .55).toFixed(3));
      plane.style.setProperty('--sm-glass-distance', state.distance.toFixed(2));
      plane.style.setProperty('--sm-glass-flow', state.weight.toFixed(4));
      plane.style.setProperty('--sm-glass-light', clamp(strength * state.weight * (1 + point.corner * .16), 0, .98).toFixed(4));
      plane.setAttribute('data-lit', '');
    });
    // A bounded settling tail, then zero animation frames while the page rests.
    if (rendered && now - lastInput < 2400 && (receiving || strength > .002 || moving)) frame = requestAnimationFrame(update);
    else stop();
  }

  function onScroll(event) {
    if (!active || document.hidden) return;
    lastSource = event.target instanceof Element ? event.target : document;
    const next = lastSource === document ? window.scrollY : lastSource.scrollTop;
    const now = performance.now();
    const previous = scrollPositions.get(lastSource);
    const elapsed = previous ? clamp(now - previous.time, 8, 100) : 16;
    lastDelta = previous ? next - previous.position : 0;
    if (!lastDelta) return;
    targetVelocity = clamp(lastDelta / elapsed * 1000, -2400, 2400);
    scrollPositions.set(lastSource, {position:next, time:now});
    sourcePosition = next; lastInput = now; dirty = true;
    if (!frame) frame = requestAnimationFrame(update);
  }

  function refresh() {
    // Breakpoint changes can remove a surface's glass material.
    [...surfaces.keys()].forEach(element => { if (!element.isConnected || !isGlass(element)) remove(element); });
    scan();
    dirty = true;
    stop();
  }

  function sync() {
    const enabled = !preferences.some(preference => preference.matches);
    if (enabled === active) return;
    active = enabled;
    if (active) {
      scan();
      window.addEventListener('scroll', onScroll, {capture:true, passive:true});
      window.addEventListener('resize', refresh, {passive:true});
    } else {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', refresh);
      stop();
      [...surfaces.keys()].forEach(remove);
    }
  }

  preferences.forEach(preference => preference.addEventListener('change', sync));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
  });
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    [...surfaces.keys()].forEach(element => { if (event.target.contains(element)) remove(element); });
  });
  // Rewards hydrate native cards; home/collection glass initializes separately.
  const mutations = new MutationObserver(records => {
    if (!active) return;
    records.forEach(record => {
      if (record.type === 'attributes') { refresh(); return; }
      record.addedNodes.forEach(node => {
        if (node instanceof Element && !node.matches('[data-sm-scroll-reflection],.sm-glass-scroll__rim,.sm-glass-scroll__light')) scan(node);
      });
    });
    [...surfaces.keys()].forEach(element => { if (!element.isConnected) remove(element); });
  });
  [document.querySelector('#MainContent'), document.querySelector('#header-group')].filter(Boolean).forEach(root => {
    mutations.observe(root, {childList:true, subtree:true, attributes:true, attributeFilter:['data-sm-glass-surface-ready','data-liquid-glass']});
  });
  window.SMGlassScroll = {refresh};
  sync();
})();
